import React, { useEffect } from "react";
import "../Common.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { makeStyles } from "@material-ui/core/styles";
import CommonHeadPart from "../../common-components/CommonHeadPart";
import DashboardIcon from "../../svg_icons/dashboard.svg";
import { ROUTES } from "../../constants/routes";
import Card from "@material-ui/core/Card";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@mui/material/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import * as Actions from "../../reducer-store/actions/dashboardAction";
import ToastMessage from "../../common-components/ToastMessage";
import { Box } from "@material-ui/core";
import { CircularProgress } from "@mui/material";
import moment from "moment";

const useStyles = makeStyles(() => ({
  dashTilesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: "16px",
    padding: "0 16px 24px",
    width: "100%",
    boxSizing: "border-box",
    "@media (max-width: 1200px)": {
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    },
    "@media (max-width: 900px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    "@media (max-width: 575px)": {
      gridTemplateColumns: "1fr",
    },
  },
  healthTileWrap: {
    gridColumn: "1 / -1",
  },
  dashCard: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    marginBottom: 0,
    boxSizing: "border-box",
    minHeight: 132,
    borderRadius: 8,
    border: "1px solid #e6e6e6",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
  },
  healthCard: {
    minHeight: 160,
  },
  divline: {
    height: "2px !important",
    borderStyle: "none !important",
    opacity: "1 !important",
    backgroundColor: "#feba26",
    margin: "0 12px",
  },
  cardCount: {
    textAlign: "center",
    fontWeight: 700,
    fontSize: "24px",
    color: "#2c2c2c",
    letterSpacing: "0.02em",
    lineHeight: 1.2,
    wordBreak: "break-word",
  },
  iconTitle: {
    flexDirection: "column",
    alignItems: "center !important",
    textAlign: "center",
    padding: "14px 12px 8px",
    flex: 1,
    "& .MuiCardHeader-avatar": {
      marginRight: "0 !important",
      marginBottom: 8,
    },
  },
  cardTitle: {
    fontSize: "13px !important",
    fontWeight: 600,
    lineHeight: "1.35",
    textAlign: "center",
    color: "#5a5a5a",
    wordBreak: "break-word",
  },
  countCenter: {
    flex: "0 0 auto",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "12px",
    minHeight: 52,
  },
  statusDot: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    display: "inline-block",
  },
  healthNote: {
    textAlign: "center",
    fontSize: 12,
    color: "#666",
    padding: "0 12px 14px",
  },
}));

const BreadCrumbDashboard = [
  { labelText: "Home", linkUrl: ROUTES.home },
  { labelText: "Dashboard", linkUrl: "" },
];

const formatCad = (amount?: number) =>
  `CAD ${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const healthColor = (status?: string) => {
  if (status === "green") return "#00a64f";
  if (status === "red") return "#e53935";
  return "#f9a825";
};

const healthLabel = (status?: string) => {
  if (status === "green") return "Healthy (Incoming > Outgoing)";
  if (status === "red") return "At Risk (Incoming < Outgoing)";
  return "Balanced (Incoming = Outgoing)";
};

type Tile = {
  title: string;
  value: React.ReactNode;
  fullWidth?: boolean;
  tooltip?: string;
  accent?: string;
};

const Dashboard: React.FC = () => {
  const classes = useStyles();
  const AuthToken = useSelector((state: any) => state.auth);
  const { token } = AuthToken;
  const dashboardInfo: any = useSelector((state: any) => state.dashboard);
  const { info } = dashboardInfo;
  const dispatch = useDispatch();
  const { DashboardData } = bindActionCreators(Actions, dispatch);

  useEffect(() => {
    (async () => {
      await DashboardData(token);
    })();
  }, []);

  const tiles: Tile[] = info
    ? [
        {
          title: "Business Health *",
          value: healthLabel(info.businessHealth),
          fullWidth: true,
          accent: healthColor(info.businessHealth),
          tooltip:
            "The accuracy of this tile depends on how accurately receipts are recorded.",
        },
        {
          title: "Total Active Students",
          value: info.totalActiveStudents ?? info.totalStudents ?? 0,
        },
        { title: "Total Assets", value: info.totalAssets ?? info.activeAssets ?? 0 },
        { title: "Disabled Assets", value: info.disabledAssets ?? 0 },
        { title: "Total Payments This Month", value: formatCad(info.paymentThisMonth) },
        { title: "Total Payments This Year", value: formatCad(info.paymentThisYear) },
        { title: "All Time Payments", value: formatCad(info.totalPayment) },
        {
          title: "DSC Status",
          value: (
            <span
              className={classes.statusDot}
              style={{ backgroundColor: healthColor(info.dscStatus) }}
              title={info.dscStatus === "green" ? "All clear" : "Payment due"}
            />
          ),
        },
        {
          title: "Total Taxes Paid This Month",
          value: formatCad(info.taxThisMonth),
        },
        {
          title: "Total Taxes Paid This Year",
          value: formatCad(info.taxThisYear),
        },
        { title: "All Time Taxes", value: formatCad(info.taxAllTime) },
        { title: "Total Bills This Month", value: formatCad(info.billsThisMonth) },
        {
          title: "Total Bills Paid This Year",
          value: formatCad(info.billsThisYear),
        },
        {
          title: "Total Bills Paid All Time",
          value: formatCad(info.billsAllTime),
        },
        { title: "Total Staff", value: info.totalStaff ?? 0 },
        { title: "Total Instructors", value: info.totalInstructors ?? 0 },
        { title: "Total Active Programs", value: info.activePrograms ?? 0 },
        { title: "Total Inactive Programs", value: info.inactivePrograms ?? 0 },
        { title: "DSC Plan", value: info.dscPlan || "--" },
        {
          title: "DSC Plan Expiry",
          value: info.dscPlanExpiry
            ? moment(info.dscPlanExpiry).format("YYYY-MM-DD")
            : "--",
        },
      ]
    : [];

  return (
    <>
      <ToastMessage />
      <CommonHeadPart
        componentText="Dashboard"
        Icon={DashboardIcon}
        buttonText=""
        urlLink="/"
        BreadCrumbs={BreadCrumbDashboard}
      />
      {!info ? (
        <Box
          style={{
            position: "absolute",
            margin: "0 auto",
            top: "50%",
            left: "50%",
            justifyContent: "center",
          }}
        >
          <CircularProgress sx={{ color: "#707070" }} />
        </Box>
      ) : (
        <div className={classes.dashTilesGrid}>
          {tiles.map((tile) => {
            const card = (
              <Card
                className={`${classes.dashCard} ${
                  tile.fullWidth ? classes.healthCard : ""
                }`}
                elevation={0}
                style={
                  tile.accent
                    ? { borderTop: `4px solid ${tile.accent}` }
                    : undefined
                }
              >
                <CardHeader
                  className={classes.iconTitle}
                  title={
                    <Typography className={classes.cardTitle}>
                      {tile.title}
                    </Typography>
                  }
                />
                <Divider color="#feba26" className={classes.divline} />
                <div className={classes.countCenter}>
                  <Typography component="div" className={classes.cardCount}>
                    {tile.value}
                  </Typography>
                </div>
                {tile.fullWidth ? (
                  <Typography className={classes.healthNote}>
                    Incoming {formatCad(info.businessHealthIncoming)} vs
                    Outgoing {formatCad(info.businessHealthOutgoing)}
                  </Typography>
                ) : null}
              </Card>
            );

            return (
              <div
                key={tile.title}
                className={tile.fullWidth ? classes.healthTileWrap : undefined}
              >
                {tile.tooltip ? (
                  <Tooltip title={tile.tooltip} arrow>
                    <div>{card}</div>
                  </Tooltip>
                ) : (
                  card
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default Dashboard;
